import type { AudioEngine } from '../../audio/AudioEngine.js';
import type { EngineSound } from '../../audio/EngineSound.js';
import type { ImpactSound } from '../../audio/ImpactSound.js';
import type { PingSound } from '../../audio/PingSound.js';
import type { TeleportWhoosh } from '../../audio/TeleportWhoosh.js';
import type { TireSound } from '../../audio/TireSound.js';
import type { TreeImpactSound } from '../../audio/TreeImpactSound.js';
import type { WindSound } from '../../audio/WindSound.js';
import {
  AUDIO_LAYER_IDS,
  AUDIO_LAYER_LABELS,
  loadAudioPrefs,
  saveAudioPrefs,
  type AudioLayerId,
  type AudioPrefs,
} from '../audioPrefs.js';
import type { SettingsSection } from '../SettingsModal.js';
import { addDivider, addSectionTitle, addSlider } from './formHelpers.js';

/** Holds the live sound-object references so the section can push volume / mute
 *  changes into the audio graph. Constructed once at boot in main.ts. */
export interface SoundsBindings {
  audio: AudioEngine;
  engine: EngineSound;
  wind: WindSound;
  teleport: TeleportWhoosh;
  tire: TireSound;
  impact: ImpactSound;
  treeImpact: TreeImpactSound;
  ping: PingSound;
}

/** Apply a full prefs object to every sound object. Called once at boot and again
 *  whenever the master mute is toggled via the M hotkey (so the slider state stays
 *  in sync with the audio engine). */
export function applyAudioPrefs(bindings: SoundsBindings, prefs: AudioPrefs): void {
  bindings.audio.setMasterVolume(prefs.master.volume);
  bindings.audio.setMuted(prefs.master.muted);
  applyLayerPref(bindings, 'engine', prefs.layers.engine);
  applyLayerPref(bindings, 'windRumble', prefs.layers.windRumble);
  applyLayerPref(bindings, 'whoosh', prefs.layers.whoosh);
  applyLayerPref(bindings, 'teleport', prefs.layers.teleport);
  applyLayerPref(bindings, 'tire', prefs.layers.tire);
  applyLayerPref(bindings, 'impact', prefs.layers.impact);
  applyLayerPref(bindings, 'treeImpact', prefs.layers.treeImpact);
  applyLayerPref(bindings, 'ping', prefs.layers.ping);
}

function applyLayerPref(
  b: SoundsBindings,
  id: AudioLayerId,
  pref: { volume: number; muted: boolean },
): void {
  switch (id) {
    case 'engine':
      b.engine.setVolume(pref.volume);
      b.engine.setMuted(pref.muted);
      return;
    case 'windRumble':
      b.wind.setRumbleVolume(pref.volume);
      b.wind.setRumbleMuted(pref.muted);
      return;
    case 'whoosh':
      b.wind.setWhooshVolume(pref.volume);
      b.wind.setWhooshMuted(pref.muted);
      return;
    case 'teleport':
      b.teleport.setVolume(pref.volume);
      b.teleport.setMuted(pref.muted);
      return;
    case 'tire':
      b.tire.setVolume(pref.volume);
      b.tire.setMuted(pref.muted);
      return;
    case 'impact':
      b.impact.setVolume(pref.volume);
      b.impact.setMuted(pref.muted);
      return;
    case 'treeImpact':
      b.treeImpact.setVolume(pref.volume);
      b.treeImpact.setMuted(pref.muted);
      return;
    case 'ping':
      b.ping.setVolume(pref.volume);
      b.ping.setMuted(pref.muted);
      return;
  }
}

/** Sounds section + a `get()` so callers (e.g. the M-key handler in main.ts) can
 *  query / mutate the persisted prefs from outside the section. The section's UI
 *  only refreshes its sliders when explicitly rebuilt — toggling the master mute
 *  via the M key updates the audio engine + prefs but doesn't rebuild the DOM
 *  (cheap and avoids resetting focused inputs). */
export function createSoundsSection(bindings: SoundsBindings): SettingsSection & {
  get(): AudioPrefs;
  refresh(): void;
} {
  let prefs = loadAudioPrefs();
  let lastParent: HTMLElement | null = null;

  // Apply persisted state to the audio graph immediately so the user hears their
  // last-session volume/mute as soon as the AudioEngine resumes.
  applyAudioPrefs(bindings, prefs);

  const persist = (): void => saveAudioPrefs(prefs);

  return {
    id: 'sounds',
    title: 'Sounds',
    get(): AudioPrefs {
      return prefs;
    },
    refresh(): void {
      if (lastParent) this.build(lastParent);
    },
    build(parent: HTMLElement): void {
      lastParent = parent;
      parent.replaceChildren();

      // One slider per layer. A slider at 0% is the mute — no separate mute checkbox.
      addSlider(
        parent,
        'Master volume',
        0,
        1,
        0.05,
        prefs.master.volume,
        (v) => `${Math.round(v * 100)}%`,
        (v) => {
          prefs.master.volume = v;
          bindings.audio.setMasterVolume(v);
          persist();
        },
      );

      addDivider(parent);
      addSectionTitle(parent, 'Layers');

      for (const id of AUDIO_LAYER_IDS) {
        const layer = prefs.layers[id];
        addSlider(
          parent,
          AUDIO_LAYER_LABELS[id],
          0,
          1,
          0.05,
          layer.volume,
          (v) => `${Math.round(v * 100)}%`,
          (v) => {
            layer.volume = v;
            applyLayerPref(bindings, id, layer);
            persist();
          },
        );
      }
    },
  };
}
